/**
 * One schema describing every column of a completed-order row — used by the
 * CSV reader/writer, the editable review grid, and the sample-template
 * generator, so all three stay in step by construction rather than by three
 * parallel lists drifting apart.
 *
 * Column order matches the Google Sheet template's own order (see
 * /google-sheets/historical-orders/Code.gs HISTORICAL_HEADERS). CSV parsing
 * matches on the header label, not position, so a sheet export with columns
 * in any order still loads — the order here is what the grid and the
 * generated template show.
 */

export const FIELD_TYPE = Object.freeze({
  MASTER: "master", // cascading dropdown backed by live master data
  SELECT: "select", // fixed option list, also from live master data
  TEXT: "text",
  NUMBER: "number",
  DATE: "date",
  FILE: "file",
});

export const FIELD_DEFS = [
  { field: "company", label: "Company", type: FIELD_TYPE.MASTER, master: "companies", width: 200 },
  { field: "agency", label: "Agency", type: FIELD_TYPE.MASTER, master: "agencies", width: 200 },
  { field: "projectCode", label: "Project Code", type: FIELD_TYPE.MASTER, master: "projects", width: 180 },
  { field: "commodity", label: "Commodity", type: FIELD_TYPE.MASTER, master: "commodities", width: 170 },
  { field: "sampleVariant", label: "Sample Variant", type: FIELD_TYPE.SELECT, options: "sampleVariant", width: 140 },
  { field: "package", label: "Package", type: FIELD_TYPE.MASTER, master: "packages", width: 220 },
  { field: "sampleType", label: "Sample Type", type: FIELD_TYPE.SELECT, options: "sampleType", width: 130 },
  { field: "sector", label: "Sector", type: FIELD_TYPE.SELECT, options: "sector", width: 130 },
  { field: "state", label: "State", type: FIELD_TYPE.MASTER, master: "states", width: 150 },
  { field: "district", label: "District", type: FIELD_TYPE.MASTER, master: "districts", width: 150 },
  { field: "sampler", label: "Sampler", type: FIELD_TYPE.TEXT, width: 150 },
  { field: "siteAddress", label: "Site Address", type: FIELD_TYPE.TEXT, width: 200 },
  { field: "batchId", label: "Batch ID", type: FIELD_TYPE.TEXT, width: 150 },
  { field: "supplier", label: "Supplier", type: FIELD_TYPE.TEXT, width: 170 },
  { field: "quantity", label: "Quantity", type: FIELD_TYPE.NUMBER, width: 110 },
  { field: "quantityUnit", label: "Quantity Unit", type: FIELD_TYPE.SELECT, options: "quantityUnit", width: 130 },
  { field: "notes", label: "Notes", type: FIELD_TYPE.TEXT, width: 200 },
  { field: "nameOfShop", label: "Name of Shop", type: FIELD_TYPE.TEXT, width: 170 },
  { field: "addressOfShop", label: "Address of Shop", type: FIELD_TYPE.TEXT, width: 220 },
  { field: "sampleWeight", label: "Sample Weight (g)", type: FIELD_TYPE.NUMBER, width: 140 },
  { field: "sampleCost", label: "Sample Cost (INR)", type: FIELD_TYPE.NUMBER, width: 140 },
  { field: "typeOfShop", label: "Type of Shop", type: FIELD_TYPE.SELECT, options: "typeOfShop", width: 150 },
  { field: "sampleSealNo", label: "Sample Seal No", type: FIELD_TYPE.TEXT, width: 160 },
  { field: "qrCodeId", label: "QR Code Id", type: FIELD_TYPE.TEXT, width: 130 },
  { field: "samplePackedIn", label: "Sample Packed In", type: FIELD_TYPE.SELECT, options: "samplePackedIn", width: 160 },
  { field: "fssaiLogoPresent", label: "FSSAI Logo Present?", type: FIELD_TYPE.SELECT, options: "fssaiLogoPresent", width: 160 },
  { field: "vegSymbolPresent", label: "Veg Symbol Present?", type: FIELD_TYPE.SELECT, options: "vegSymbolPresent", width: 160 },
  { field: "assignedLab", label: "Assigned Lab", type: FIELD_TYPE.MASTER, master: "labs", width: 230 },
  { field: "assignedLogistics", label: "Assigned Logistics", type: FIELD_TYPE.MASTER, master: "logistics", width: 180 },
  { field: "trackingId", label: "Tracking Id", type: FIELD_TYPE.TEXT, width: 140 },
  { field: "samplePhysicalCondition", label: "Sample Physical Condition", type: FIELD_TYPE.SELECT, options: "samplePhysicalCondition", width: 180 },
  { field: "conditionOfSeal", label: "Condition of Seal", type: FIELD_TYPE.SELECT, options: "conditionOfSeal", width: 160 },
  { field: "sampleRegistrationNumber", label: "Sample Registration Number", type: FIELD_TYPE.TEXT, width: 190 },
  { field: "dateOfPurchase", label: "Date of Purchase", type: FIELD_TYPE.DATE, width: 150 },
  { field: "pendingDate", label: "Pending Date", type: FIELD_TYPE.DATE, width: 150 },
  { field: "bookedDate", label: "Booked Date", type: FIELD_TYPE.DATE, width: 150 },
  { field: "inReviewDate", label: "In Review Date", type: FIELD_TYPE.DATE, width: 150 },
  { field: "readyForPickupDate", label: "Ready for Pickup Date", type: FIELD_TYPE.DATE, width: 170 },
  { field: "outForPickupDate", label: "Out for Pickup Date", type: FIELD_TYPE.DATE, width: 170 },
  { field: "deliveredToLabDate", label: "Delivered to Lab Date", type: FIELD_TYPE.DATE, width: 170 },
  { field: "receivedAtLabDate", label: "Received at Lab Date", type: FIELD_TYPE.DATE, width: 170 },
  { field: "underTestingDate", label: "Under Testing Date", type: FIELD_TYPE.DATE, width: 160 },
  { field: "expectedDateOfCompletion", label: "Expected Date of Completion", type: FIELD_TYPE.DATE, width: 190 },
  { field: "reportUploadedDate", label: "Report Uploaded Date", type: FIELD_TYPE.DATE, width: 170 },
  { field: "targetEndStatus", label: "Target End Status", type: FIELD_TYPE.SELECT, options: "targetEndStatus", width: 160 },
  { field: "completedDate", label: "Completed Date", type: FIELD_TYPE.DATE, width: 150 },
  // Attached here in the grid, never named in the sheet — so these are grid
  // columns only and deliberately absent from CSV_COLUMNS below.
  { field: "reportFile", label: "Report File", type: FIELD_TYPE.FILE, width: 180 },
  { field: "reportMetadataFile", label: "Report Metadata File", type: FIELD_TYPE.FILE, width: 180 },
  // Every optional single-photo field on be/models/sampleDetails.model.js.
  // All genuinely optional — an order with none of these attached is exactly
  // as valid as one with all of them, same as on a real live order entered
  // by hand. photographOfShop/generalPackingPhoto aren't here: those are
  // multi-photo galleries, a different shape than one cell can hold.
  { field: "photographOfInvoice", label: "Photograph of Invoice", type: FIELD_TYPE.FILE, width: 180 },
  { field: "validityPhoto", label: "Expiry Date Photo", type: FIELD_TYPE.FILE, width: 180 },
  { field: "manufacturerNamePhoto", label: "Manufacturers Name Photo", type: FIELD_TYPE.FILE, width: 200 },
  { field: "packagingTypePhoto", label: "Packaging Image", type: FIELD_TYPE.FILE, width: 180 },
  { field: "fssaiLogoPhoto", label: "FSSAI Logo Photo", type: FIELD_TYPE.FILE, width: 180 },
  { field: "vegSymbolPhoto", label: "Veg/Non-veg Symbol Photo", type: FIELD_TYPE.FILE, width: 200 },
  { field: "jaivikBharatSymbolPhoto", label: "Jaivik Bharat Symbol Photo", type: FIELD_TYPE.FILE, width: 200 },
  { field: "qrCodePhoto", label: "Sample Photo with QR Code", type: FIELD_TYPE.FILE, width: 200 },
  { field: "sampleSealPhoto", label: "Sample Seal Photo", type: FIELD_TYPE.FILE, width: 180 },
  { field: "samplerSelfie", label: "Sampler Selfie", type: FIELD_TYPE.FILE, width: 180 },
  { field: "fssaiLicenseNumberPhoto", label: "Photograph of FSSAI License Number", type: FIELD_TYPE.FILE, width: 220 },
  { field: "sampleClaimPhoto", label: "Photograph of Sample Claim", type: FIELD_TYPE.FILE, width: 200 },
  { field: "overallSafetyCompliant", label: "Overall Safety Compliant", type: FIELD_TYPE.SELECT, options: "overallSafetyCompliant", width: 180 },
  { field: "overallQualityCompliant", label: "Overall Quality Compliant", type: FIELD_TYPE.SELECT, options: "overallQualityCompliant", width: 180 },
  { field: "overallLabellingCompliant", label: "Overall Labelling Compliant", type: FIELD_TYPE.SELECT, options: "overallLabellingCompliant", width: 190 },
  { field: "overallSampleClassification", label: "Overall Sample Classification", type: FIELD_TYPE.SELECT, options: "overallSampleClassification", width: 200 },
];

export const FIELD_DEF_BY_FIELD = FIELD_DEFS.reduce((map, def) => {
  map[def.field] = def;
  return map;
}, {});

// The optional SampleDetails photo fields specifically — every FILE column
// except the two report documents, which are required and handled on their
// own. Derived from FIELD_DEFS rather than listed again so this can't drift
// from the columns actually defined above (or from
// be/utils/uploadFile.js's HISTORICAL_ORDER_SAMPLE_PHOTO_FIELDS, which uses
// the exact same field names).
export const SAMPLE_PHOTO_FIELDS = FIELD_DEFS
  .filter((def) => def.type === FIELD_TYPE.FILE && def.field !== "reportFile" && def.field !== "reportMetadataFile")
  .map((def) => def.field);

// File columns exist only in the grid — the sheet has no filename columns to
// export, so they're skipped here rather than expected in an upload.
export const CSV_COLUMNS = FIELD_DEFS
  .filter((def) => def.type !== FIELD_TYPE.FILE)
  .map((def) => [def.label, def.field]);

// Changing a parent invalidates everything scoped underneath it — same rule
// the sheet and the server both apply, kept here so an inline edit in the
// grid can't leave a stale child behind.
export const DOWNSTREAM_FIELDS = Object.freeze({
  company: ["projectCode", "commodity", "package"],
  projectCode: ["commodity", "package"],
  commodity: ["package"],
  sampleVariant: ["package"],
  state: ["district"],
});

// The columns each cascaded column is actually scoped by — the inverse of
// DOWNSTREAM_FIELDS, kept explicit so an empty dropdown can name exactly
// which columns still need filling instead of vaguely pointing upstream.
export const PARENT_FIELDS = Object.freeze({
  projectCode: ["company"],
  commodity: ["projectCode"],
  package: ["company", "commodity", "sampleVariant"],
  district: ["state"],
});

// Which of this cell's parent columns are still blank, in column order.
export function missingParentsFor(field, row) {
  return (PARENT_FIELDS[field] || [])
    .filter((parent) => !String(row[parent] || "").trim());
}

// The parent values this cell is currently scoped by — what to name when the
// list is empty despite every parent being filled, since then the honest
// answer is "nothing exists for this combination", not "fill something in".
export function parentScopeSummary(field, row) {
  return (PARENT_FIELDS[field] || [])
    .map((parent) => String(row[parent] || "").trim())
    .filter(Boolean)
    .join(" + ");
}

export function labelsFor(fields) {
  return fields.map((field) => (FIELD_DEF_BY_FIELD[field] ? FIELD_DEF_BY_FIELD[field].label : field));
}

export function joinLabels(labels) {
  if (labels.length <= 1) return labels[0] || "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

// The lifecycle stages in order. Each one may not be earlier than the nearest
// filled stage behind it, nor later than the nearest filled stage ahead of it
// (skipping blanks) — the same rule AddCompletedOrder applies live and
// historicalOrderRowValidation.service.js enforces server-side, so an
// out-of-order date can't be picked here in the first place.
export const DATE_CHAIN_FIELDS = Object.freeze([
  "dateOfPurchase", "pendingDate", "bookedDate", "inReviewDate", "readyForPickupDate",
  "outForPickupDate", "deliveredToLabDate", "receivedAtLabDate", "underTestingDate",
  "reportUploadedDate", "completedDate",
]);

// Expected Date of Completion sits outside that strict chain — it's a
// forward-looking target set once the sample reaches the lab, so it gets only
// a LOWER bound (the nearest filled stage at/behind Under Testing). No upper
// bound: a real target can legitimately be missed by the time the report
// actually lands.
export const EXPECTED_COMPLETION_ANCHORS = Object.freeze([
  "underTestingDate", "receivedAtLabDate", "deliveredToLabDate", "outForPickupDate",
  "readyForPickupDate", "inReviewDate", "bookedDate", "pendingDate", "dateOfPurchase",
]);

function nearestFilled(row, fromIndex, direction) {
  for (let i = fromIndex + direction; i >= 0 && i < DATE_CHAIN_FIELDS.length; i += direction) {
    if (row[DATE_CHAIN_FIELDS[i]]) return row[DATE_CHAIN_FIELDS[i]];
  }
  return null;
}

function todayYmd() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

// This grid only ever enters orders that have already happened, so NO date
// field can be in the future — every stage, not just the terminal one.
// Capping only Report Uploaded/Completed Date and relying on the
// neighbor-bound to propagate that backwards was the earlier approach, and
// it was wrong: that only actually constrains earlier dates once a LATER one
// is filled in. Filling a row left-to-right (its natural column order) never
// touches a later date first, so nothing stopped Booked/In Review/etc. from
// drifting arbitrarily far into the future one cell at a time — right up
// until Report Uploaded Date, capped at today, by which point the row is
// already self-contradicting. Capping every stage independently (and
// Expected Date of Completion, which isn't part of the chain but is still a
// fact recorded about an order that's already fully in the past) closes that
// regardless of fill order.
/**
 * The min/max a given date cell is allowed to hold, given what the rest of
 * that row currently has filled in. Returns plain YYYY-MM-DD strings (or
 * null) — the caller turns them into whatever its picker wants.
 */
export function dateBoundsForField(field, row) {
  const today = todayYmd();
  if (field === "expectedDateOfCompletion") {
    const min = EXPECTED_COMPLETION_ANCHORS.map((key) => row[key]).find(Boolean) || null;
    return { min, max: today };
  }
  const index = DATE_CHAIN_FIELDS.indexOf(field);
  if (index < 0) return { min: null, max: null };
  const min = nearestFilled(row, index, -1);
  let max = nearestFilled(row, index, 1);
  if (!max || today < max) max = today;
  return { min, max };
}

/**
 * Setting one date can strand later ones that were valid when they were
 * picked but aren't anymore. Returns just the fields that are now out of
 * order and need clearing, so the caller can report how many it wiped.
 */
export function datesInvalidatedBy(field, value, row, isBefore) {
  if (!value) return [];
  const stranded = [];
  const index = DATE_CHAIN_FIELDS.indexOf(field);
  if (index >= 0) {
    for (let i = index + 1; i < DATE_CHAIN_FIELDS.length; i++) {
      const later = DATE_CHAIN_FIELDS[i];
      if (row[later] && isBefore(row[later], value)) stranded.push(later);
    }
  }
  if (row.expectedDateOfCompletion && EXPECTED_COMPLETION_ANCHORS.includes(field) &&
      isBefore(row.expectedDateOfCompletion, value)) {
    stranded.push("expectedDateOfCompletion");
  }
  return stranded;
}

/**
 * The live option list for one cell, scoped by whatever that row's parent
 * cells currently hold — deliberately the same scoping the server validates
 * against, so the grid can never offer a combination the server will reject.
 */
export function optionsForField(def, row, masterData) {
  if (!masterData) return [];

  if (def.type === FIELD_TYPE.SELECT) {
    const options = masterData.options || [];
    if (def.options === "targetEndStatus") {
      const project = (masterData.projects || []).find((p) => p.code === row.projectCode);
      const scoped = options.filter((o) => o.field === "targetEndStatus" && o.project_id === (project ? project.id : null));
      const list = scoped.length
        ? scoped
        : options.filter((o) => o.field === "targetEndStatus" && !o.project_id);
      return list.sort((a, b) => (a.sequence || 0) - (b.sequence || 0)).map((o) => o.value);
    }
    return options
      .filter((o) => o.field === def.options && !o.project_id)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      .map((o) => o.value);
  }

  if (def.type !== FIELD_TYPE.MASTER) return [];

  // A cascaded column offers nothing until the columns that scope it are
  // filled — the same rule Code.gs applies in the sheet, where a blank parent
  // yields an empty list rather than an unscoped one. Without this, Package
  // would list every package in the system before anyone has said which
  // company or commodity it belongs to, and the caller could never tell the
  // user which column to fill in first.
  if (missingParentsFor(def.field, row).length) return [];

  const companies = masterData.companies || [];
  const projects = masterData.projects || [];
  const commodities = masterData.commodities || [];
  const company = companies.find((c) => c.name === row.company);
  const project = projects.find((p) => p.code === row.projectCode);
  const commodity = commodities.find((c) => c.name === row.commodity && (!project || c.project_id === project.id));
  const state = (masterData.states || []).find((s) => s.name === row.state);

  switch (def.master) {
    case "companies":
      return companies.map((c) => c.name);
    // Agency, Lab and Logistics genuinely aren't scoped by company or
    // location in this app's schema — flat lists, verified against the real
    // data rather than assumed.
    case "agencies":
      return (masterData.agencies || []).map((a) => a.name);
    case "labs":
      return (masterData.labs || []).map((l) => l.name);
    case "logistics":
      return (masterData.logistics || []).map((l) => l.name);
    case "projects":
      return projects.filter((p) => !company || p.company_id === company.id).map((p) => p.code);
    case "commodities":
      return unique(commodities.filter((c) => !project || c.project_id === project.id).map((c) => c.name));
    case "packages":
      return unique((masterData.packages || [])
        .filter((p) => {
          if (company && p.company_id !== company.id) return false;
          if (commodity && p.commodity_id !== commodity.id) return false;
          if (row.sampleVariant && p.variant !== row.sampleVariant) return false;
          return true;
        })
        .map((p) => p.name));
    case "states":
      return (masterData.states || []).map((s) => s.name);
    case "districts":
      return (masterData.districts || [])
        .filter((d) => !state || d.state_id === state.id)
        .map((d) => d.name);
    default:
      return [];
  }
}

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}
